import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Card, Form, Button } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { getVoucherGroup, issueVoucher } from '../../travel/VoucherGroups/api/VoucherGroupApi';
import EmployeeSearch from '../../common/empsearch/EmployeeSearch';

const schema = yup.object({
  voucherName: yup.string().required('Voucher name is required'),
  employeeId: yup.number().required('Employee is required'),
  issueDate: yup.date().required('Issue date is required'),
  expiresAfterInYears: yup
    .number()
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .when('doesNotExpire', {
      is: false,
      then: () => yup.number()
        .required('Expiration years is required')
        .positive('Must be a positive number')
        .integer('Must be a whole number'),
      otherwise: () => yup.number().nullable()
    }),
  doesNotExpire: yup.boolean(),
  initialAmount: yup
    .number()
    .typeError('Must be a number')
    .positive('Must be a positive amount')
    .required('Initial amount is required')
}).required();

const AddVoucherForm = () => {
  const history = useHistory();
  const { voucherGroupId } = useParams();
  const [voucherGroup, setVoucherGroup] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doesNotExpire, setDoesNotExpire] = useState(false);
  const [calculatedExpirationDate, setCalculatedExpirationDate] = useState(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      doesNotExpire: false,
      expiresAfterInYears: null
    }
  });

  const issueDate = watch('issueDate');
  const expiresAfterInYears = watch('expiresAfterInYears');

  const calculateIssueDate = (employee, group) => {
    if (!employee || !group) return new Date().toISOString().split('T')[0];

    if (group.issueDateTypeId === 'GROUPSTARTORHIRE') {
      const groupStartDate = new Date(group.startDate);
      const hireDate = new Date(employee.lastHireDate);
      return (hireDate > groupStartDate ? hireDate : groupStartDate).toISOString().split('T')[0];
    } else if (group.issueDateTypeId === 'SENIORITYDATEANNIV') {
      const seniorityDate = new Date(employee.allowanceSeniorityDate);
      const anniversaryDate = new Date(seniorityDate);
      anniversaryDate.setFullYear(anniversaryDate.getFullYear() + group.anniversaryYearsCriteria);
      return anniversaryDate.toISOString().split('T')[0];
    }

    return new Date().toISOString().split('T')[0];
  };

  useEffect(() => {
    const fetchVoucherGroup = async () => {
      try {
        const group = await getVoucherGroup(voucherGroupId);
        setVoucherGroup(group);
        setDoesNotExpire(!group.defaultExpiresAfterInYears);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching voucher group:", err);
        setError(err);
        setLoading(false);
      }
    };

    fetchVoucherGroup();
  }, [voucherGroupId]);

  useEffect(() => {
    if (issueDate && expiresAfterInYears && !doesNotExpire) {
      const issueDateTime = new Date(issueDate);
      const expirationDate = new Date(issueDateTime.setFullYear(issueDateTime.getFullYear() + parseInt(expiresAfterInYears)));
      setCalculatedExpirationDate(expirationDate.toISOString().split('T')[0]);
    } else {
      setCalculatedExpirationDate(null);
    }
  }, [issueDate, expiresAfterInYears, doesNotExpire]);

  const handleEmployeeChange = (employee) => {
    setSelectedEmployee(employee);
    setValue('employeeId', employee.employeeId);

    if (voucherGroup) {
      // Set default values after employee is selected
      const defaultIssueDate = calculateIssueDate(employee, voucherGroup);
      
      reset({
        employeeId: employee.employeeId,
        voucherName: voucherGroup.groupName,
        issueDate: defaultIssueDate,
        expiresAfterInYears: voucherGroup.defaultExpiresAfterInYears,
        initialAmount: voucherGroup.defaultInitialAmount,
        doesNotExpire: !voucherGroup.defaultExpiresAfterInYears
      });
    }
  };

  const handleExpirationChange = (value) => {
    setDoesNotExpire(value);
    setValue('doesNotExpire', value);
    if (value) {
      setValue('expiresAfterInYears', null);
      setCalculatedExpirationDate(null);
    } else if (voucherGroup) {
      setValue('expiresAfterInYears', voucherGroup.defaultExpiresAfterInYears);
    }
  };

  const handleIssue = async (data) => {
    try {
      const submitData = {
        ...data,
        expiresAfterInYears: doesNotExpire ? undefined : data.expiresAfterInYears
      };

      // Remove expiresAfterInYears completely from the payload if voucher doesn't expire
      if (doesNotExpire) {
        delete submitData.expiresAfterInYears;
      }

      await issueVoucher(voucherGroupId, submitData);
      history.push(`/travel/vouchergroups/${voucherGroupId}`);
    } catch (error) {
      console.error("Error issuing voucher:", error);
      setError(error);
    }
  };

  const handleCancel = () => {
    history.push(`/travel/vouchergroups/${voucherGroupId}`);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <Card className="mx-auto" style={{ maxWidth: '30rem' }}>
      <Card.Body>
        <Card.Title>Issue New Allowance</Card.Title>
        <Card.Subtitle className="mb-3 text-muted">
          Group: {voucherGroup?.groupName}
        </Card.Subtitle>
        <Form noValidate onSubmit={handleSubmit(handleIssue)}>
          <Form.Group className="mb-3">
            <Form.Label>Employee</Form.Label>
            <input type="hidden" {...register('employeeId')} />
            <EmployeeSearch 
              empChangeHandler={handleEmployeeChange}
            />
            {errors.employeeId && (
              <div className="text-danger mt-1">
                {errors.employeeId.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Allowance Name</Form.Label>
            <Form.Control
              type="text"
              isInvalid={!!errors.voucherName}
              {...register('voucherName')}
              placeholder="Enter voucher name"
              readOnly={!selectedEmployee}
            />
            <Form.Control.Feedback type="invalid">
              {errors.voucherName?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Issue Date</Form.Label>
            <Form.Control
              type="date"
              isInvalid={!!errors.issueDate}
              {...register('issueDate')}
              readOnly={!selectedEmployee}
            />
            <Form.Control.Feedback type="invalid">
              {errors.issueDate?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Expiration</Form.Label>
            <div className="d-flex flex-column gap-2">
              <Form.Check
                type="radio"
                label="Expires after"
                id="expires-after"
                checked={!doesNotExpire}
                onChange={() => handleExpirationChange(false)}
                disabled={!selectedEmployee}
              />
              {!doesNotExpire && (
                <div className="ms-4">
                  <div className="mb-2">
                    <Form.Control
                      type="number"
                      placeholder="Years"
                      style={{ width: '100px' }}
                      isInvalid={!!errors.expiresAfterInYears}
                      {...register('expiresAfterInYears')}
                      readOnly={!selectedEmployee}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.expiresAfterInYears?.message}
                    </Form.Control.Feedback>
                  </div>
                  {calculatedExpirationDate && (
                    <Form.Control
                      type="date"
                      value={calculatedExpirationDate}
                      readOnly
                      plaintext
                    />
                  )}
                </div>
              )}
              <Form.Check
                type="radio"
                label="Does not expire"
                id="does-not-expire"
                checked={doesNotExpire}
                onChange={() => handleExpirationChange(true)}
                disabled={!selectedEmployee}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Initial Amount</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              isInvalid={!!errors.initialAmount}
              {...register('initialAmount')}
              readOnly={!selectedEmployee}
            />
            <Form.Control.Feedback type="invalid">
              {errors.initialAmount?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button variant="primary" type="submit" disabled={!selectedEmployee}>
              Issue Allowance
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddVoucherForm;